// PostHog: boot from the layout's meta tags, and keep the identity in step
// with the session on every Turbo visit.
//
// The layout emits current-user-id/-email meta tags only when signed in, and
// Turbo swaps head metas on navigation — so "meta present" IS the auth state:
// present and different from the current distinct id -> identify; absent while
// identified -> the session ended, reset to a fresh anonymous id. The guard
// against resetting anonymous visitors matters: reset() mints a new distinct
// id, and calling it every pageview would shred anonymous session stitching.
import posthog from "posthog-js";

const meta = (name: string): string | null =>
	document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content ||
	null;

const boot = (): boolean => {
	const key = meta("posthog-key");
	if (!key) return false;

	if (!posthog.__loaded) {
		posthog.init(key, {
			api_host: meta("posthog-host") || "https://eu.i.posthog.com",
			// Turbo drives navigation through the History API, so pageviews come
			// from history changes, not full loads.
			capture_pageview: "history_change",
			// Anonymous traffic stays event-only (cheaper, less data held); person
			// profiles exist only once someone signs in and we identify them.
			person_profiles: "identified_only",
		});
	}
	return true;
};

const syncIdentity = (): void => {
	const id = meta("current-user-id");
	if (id) {
		if (posthog.get_distinct_id() !== id) {
			posthog.identify(id, { email: meta("current-user-email") });
		}
	} else if (posthog._isIdentified()) {
		posthog.reset();
	}
};

if (boot()) {
	syncIdentity();
	document.addEventListener("turbo:load", syncIdentity);
}
